export function formatNumber(value: unknown, fallback = "0"): string {
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("zh-CN").format(number) : fallback;
}

export function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = typeof value === "number" || /^\d+(\.\d+)?$/.test(String(value)) ? Number(value) : null;
  const milliseconds = numeric !== null ? (numeric < 10_000_000_000 ? numeric * 1000 : numeric) : value;
  const date = new Date(milliseconds as string | number);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

export function formatDuration(seconds: unknown): string {
  const value = Number(seconds);
  if (!Number.isFinite(value)) return "-";
  if (value < 60) return `${Math.round(value)}s`;
  const minutes = Math.floor(value / 60);
  const remainder = Math.round(value % 60);
  return `${minutes}m ${remainder}s`;
}

export function formatBytes(bytes: unknown): string {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return "-";
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let current = value / 1024;
  let index = 0;
  while (current >= 1024 && index < units.length - 1) {
    current /= 1024;
    index += 1;
  }
  return `${current >= 10 ? current.toFixed(0) : current.toFixed(1)} ${units[index]}`;
}

export function truncate(value: unknown, length = 56): string {
  const text = String(value ?? "");
  return text.length > length ? `${text.slice(0, length - 1)}...` : text;
}

export function errorText(error: unknown): string {
  if (!error) return "-";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Format an API/worker failure for visible management-console copy.
 * Machine error codes remain useful to the API client, but are intentionally
 * not rendered as English text in the operator-facing UI.
 */
export function displayError(message: unknown, code?: unknown): string {
  if (message && typeof message === "object" && !Array.isArray(message)) {
    const record = message as Record<string, unknown>;
    const reason = record.reason;
    if (typeof reason === "string" && /[\u3400-\u9fff]/.test(reason) && reason.trim()) {
      return reason.trim();
    }
    const nestedMessage = record.message ?? record.detail ?? record.error ?? record.msg;
    const nestedCode = code ?? record.code ?? record.error_code;
    if (nestedMessage !== undefined) return displayError(nestedMessage, nestedCode);
  }
  const text = errorText(message).trim();
  if (/[\u3400-\u9fff]/.test(text)) return text;
  const normalized = String(code ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "_");
  if (/(?:login|auth|credential|session|account)/.test(normalized)) {
    return "登录失败，请稍后重试";
  }
  if (/(?:quota|credit|entitlement|membership|subscription|free_generation)/.test(normalized)) {
    return "操作未完成，请稍后重试";
  }
  if (/(?:timeout|deadline|lease_expired)/.test(normalized)) {
    return "操作超时，请稍后重试";
  }
  if (/(?:challenge|captcha)/.test(normalized)) {
    return "操作失败，请稍后重试";
  }
  if (/(?:network|transport|unavailable|service)/.test(normalized)) {
    return "服务暂时不可用，请稍后重试";
  }
  if (/(?:failed|error|rejected|invalid)/.test(normalized) || text) {
    return "操作失败，请稍后重试";
  }
  return "操作失败，请稍后重试";
}

export function displayBatchMessage(message: unknown): string {
  const normalized = String(message ?? "").trim().toLowerCase();
  const known: Record<string, string> = {
    project_created_quota_verified: "项目已初始化，额度验证通过",
    project_created_quota_below_threshold: "初始化未完成，请稍后重试",
    proxy_identity_failed: "代理身份验证失败，请稍后重试",
    signup_password_not_configured: "注册配置不完整，请检查后重试",
    identity_probe_failed: "账号身份验证失败，请稍后重试",
    identity_mismatch: "账号身份验证失败，请稍后重试",
  };
  if (known[normalized]) return known[normalized];
  return displayError(message);
}

export function statusTone(status: unknown): "success" | "danger" | "warning" | "info" | "neutral" {
  const value = String(status ?? "").toUpperCase();
  if (["SUCCEEDED", "ACTIVE", "LIVE", "HEALTHY", "OK"].includes(value)) return "success";
  if (["FAILED", "TIMED_OUT", "EXPIRED", "CANCELLED", "DISABLED", "STALE", "ERROR", "CRITICAL", "REAUTH_REQUIRED"].includes(value)) return "danger";
  if (["RETRY_WAIT", "UNKNOWN_REMOTE", "WAITING_SESSION", "CANCEL_REQUESTED", "CHALLENGE_REQUIRED", "COOLDOWN", "WARNING", "WARN", "MAINTENANCE"].includes(value)) return "warning";
  if (["RUNNING", "SUBMITTING", "PREPARING", "QUEUED", "INFO", "DEBUG", "PREPARE", "CREATE", "BROWSER", "POLL"].includes(value)) return "info";
  return "neutral";
}
